import type { ProcurementKind } from './catalog-client';

/**
 * The /results card («Что вы поставляете?») — its state, its URL and its
 * criteria, in one place.
 *
 * ## One state, three spellings
 *
 *  - the URL (`/results?q=рис,мука&category=food&region=SAM,JIZ&…`) — what a
 *    shared link, the prerender, the browser's back button and the alert's
 *    «+N ещё» link carry. Every chip list is comma-joined in its own param;
 *    the API builds the same URL for its alerts (`buildResultsUrl` in
 *    `services/api/src/modules/notifications/results-alert-format.ts`).
 *  - the card state ({@link ResultsCardState}) the component edits;
 *  - the criteria object the API filters the list with (`criteria=` JSON) and
 *    stores as the reader's ONE `results` saved search — the same
 *    `SavedSearchCriteria` shape lots searches use, so one compiler serves the
 *    list, the count line and the alert.
 *
 * Unknown or malformed URL values degrade to "no chip", never to an error: the
 * API sanitizes again (`sanitizeResultsCriteria`), but a stale or hand-edited
 * link must not blank a public page.
 */

/**
 * /results is the last MONTH of outcomes, newest first (owner, 2026-10-07) —
 * older awards stay on their lot and company pages. Mirrors the API's
 * `RESULTS_WINDOW_DAYS` / `RESULTS_SORT` (services/api/.../results-search.ts).
 * ONE definition for the list, its SSR seed key and the prerender's
 * `/results/page/N` queries, so the three cannot disagree on what page N holds.
 */
export const RESULTS_WINDOW_DAYS = 30;
export const RESULTS_SORT = '-outcomeAt';
/** The /results list query (without paging/lang), as the prerender sends it. */
export const RESULTS_LIST_QUERY = `status=closed&awarded=true&outcomeDays=${RESULTS_WINDOW_DAYS}&sort=${RESULTS_SORT}`;

/** Most chips of one kind — the API's own cap (`RESULTS_MAX_CHIPS`). */
export const RESULTS_MAX_CHIPS = 10;
/** Longest word chip (`RESULTS_TERM_MAX_LEN`). */
export const RESULTS_TERM_MAX_LEN = 64;

export interface ResultsCardState {
  /** Free words, any of which may match (title, description, documents). */
  words: string[];
  /** Category slugs — union with `ikpu`. */
  categories: string[];
  /** ИКПУ codes (hierarchical) — union with `categories`. */
  ikpu: string[];
  regions: string[];
  kinds: ProcurementKind[];
  /** «Не присылать со словами». */
  exclude: string[];
  /** One buyer ИНН. */
  customer?: string;
  minBudget?: number;
  maxBudget?: number;
}

/** The criteria the API takes as `criteria=` and stores — `SavedSearchCriteria`. */
export interface ResultsCriteria {
  v: 1;
  include?: {
    text?: string[];
    category?: string[];
    ikpu?: string[];
    region?: string[];
    kind?: ProcurementKind[];
    customerTin?: string[];
  };
  exclude?: { text?: string[] };
  budgetUzs?: { min?: number; max?: number };
}

export const EMPTY_RESULTS_STATE: ResultsCardState = {
  words: [],
  categories: [],
  ikpu: [],
  regions: [],
  kinds: [],
  exclude: [],
};

const KINDS: readonly ProcurementKind[] = ['goods', 'works', 'services'];
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
/** A UZ dotted code, or a CPV code (Spain: exactly eight digits). Mirrors the
 *  API's `results-search.ts` sanitizer, which would otherwise drop it. */
const IKPU_RE = /^(?:\d{2}(\.\d{1,3}){0,4}|\d{8})$/;
const REGION_RE = /^[A-Za-z0-9_-]{1,32}$/;
const TIN_RE = /^\d{9,14}$/;

/** A word chip as typed: trimmed, inner whitespace collapsed, or `null` when
 *  it cannot be one (empty, too long, carries the URL's list separator). */
export function normalizeWord(raw: string): string | null {
  const w = raw.trim().replace(/\s+/g, ' ');
  if (!w || w.length > RESULTS_TERM_MAX_LEN || w.includes(',')) return null;
  return w;
}

function list(raw: string | null, ok: (s: string) => boolean): string[] {
  if (!raw) return [];
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const s = part.trim().replace(/\s+/g, ' ');
    if (!s || !ok(s) || out.includes(s)) continue;
    out.push(s);
    if (out.length >= RESULTS_MAX_CHIPS) break;
  }
  return out;
}

function bound(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === '' || !/^\d+$/.test(raw.trim())) return undefined;
  const n = Number(raw.trim());
  return Number.isSafeInteger(n) ? n : undefined;
}

/** URL → card state. */
export function parseResultsParams(sp: URLSearchParams): ResultsCardState {
  const tin = sp.get('customer')?.trim();
  return {
    words: list(sp.get('q'), (s) => normalizeWord(s) !== null),
    categories: list(sp.get('category'), (s) => SLUG_RE.test(s)),
    ikpu: list(sp.get('ikpu'), (s) => IKPU_RE.test(s)),
    regions: list(sp.get('region'), (s) => REGION_RE.test(s)),
    kinds: list(sp.get('kind'), (s) =>
      (KINDS as readonly string[]).includes(s),
    ) as ProcurementKind[],
    exclude: list(sp.get('exclude'), (s) => normalizeWord(s) !== null),
    ...(tin && TIN_RE.test(tin) ? { customer: tin } : {}),
    ...(bound(sp.get('minBudget')) !== undefined ? { minBudget: bound(sp.get('minBudget')) } : {}),
    ...(bound(sp.get('maxBudget')) !== undefined ? { maxBudget: bound(sp.get('maxBudget')) } : {}),
  };
}

const CARD_PARAMS = [
  'q',
  'category',
  'ikpu',
  'region',
  'kind',
  'exclude',
  'customer',
  'minBudget',
  'maxBudget',
  // Owned by the old filter bar, which /results no longer shows — dropped on
  // the first card edit so a stale `?niche=`/`?status=` cannot linger unseen.
  'niche',
  'status',
  'sort',
  'deadlineFrom',
  'deadlineTo',
  'page',
] as const;

/** Card state → URL params. Starts from `base` so unrelated params (utm, src)
 *  survive; every card param is rewritten and the page resets to 1. */
export function writeResultsParams(
  state: ResultsCardState,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const k of CARD_PARAMS) next.delete(k);
  const set = (k: string, v: readonly string[]) => {
    if (v.length > 0) next.set(k, v.join(','));
  };
  set('q', state.words);
  set('category', state.categories);
  set('ikpu', state.ikpu);
  set('region', state.regions);
  set('kind', state.kinds);
  set('exclude', state.exclude);
  if (state.customer) next.set('customer', state.customer);
  if (state.minBudget !== undefined) next.set('minBudget', String(state.minBudget));
  if (state.maxBudget !== undefined) next.set('maxBudget', String(state.maxBudget));
  return next;
}

/** Is there any card state at all? */
export function hasResultsState(s: ResultsCardState): boolean {
  return (
    s.words.length > 0 ||
    s.categories.length > 0 ||
    s.ikpu.length > 0 ||
    s.regions.length > 0 ||
    s.kinds.length > 0 ||
    s.exclude.length > 0 ||
    !!s.customer ||
    s.minBudget !== undefined ||
    s.maxBudget !== undefined
  );
}

/** Card state → criteria, or `null` for an empty card (no filter / nothing to save). */
export function toResultsCriteria(s: ResultsCardState): ResultsCriteria | null {
  if (!hasResultsState(s)) return null;
  const include: NonNullable<ResultsCriteria['include']> = {};
  if (s.words.length) include.text = [...s.words];
  if (s.categories.length) include.category = [...s.categories];
  if (s.ikpu.length) include.ikpu = [...s.ikpu];
  if (s.regions.length) include.region = [...s.regions];
  if (s.kinds.length) include.kind = [...s.kinds];
  if (s.customer) include.customerTin = [s.customer];
  const c: ResultsCriteria = { v: 1 };
  if (Object.keys(include).length) c.include = include;
  if (s.exclude.length) c.exclude = { text: [...s.exclude] };
  if (s.minBudget !== undefined || s.maxBudget !== undefined) {
    c.budgetUzs = {
      ...(s.minBudget !== undefined ? { min: s.minBudget } : {}),
      ...(s.maxBudget !== undefined ? { max: s.maxBudget } : {}),
    };
  }
  return c;
}

/** The `criteria=` param value, or undefined for an empty card. Stable key
 *  order (built field by field above), so equal states give equal strings —
 *  it is part of the list's react-query key. */
export function resultsCriteriaParam(s: ResultsCardState): string | undefined {
  const c = toResultsCriteria(s);
  return c ? JSON.stringify(c) : undefined;
}

/** A saved search's criteria → card state (the pre-fill and the diff). */
export function fromResultsCriteria(c: ResultsCriteria | null | undefined): ResultsCardState {
  const inc = c?.include ?? {};
  const min = c?.budgetUzs?.min;
  const max = c?.budgetUzs?.max;
  return {
    words: [...(inc.text ?? [])],
    categories: [...(inc.category ?? [])],
    ikpu: [...(inc.ikpu ?? [])],
    regions: [...(inc.region ?? [])],
    kinds: [...(inc.kind ?? [])],
    exclude: [...(c?.exclude?.text ?? [])],
    ...(inc.customerTin?.[0] ? { customer: inc.customerTin[0] } : {}),
    ...(typeof min === 'number' ? { minBudget: min } : {}),
    ...(typeof max === 'number' ? { maxBudget: max } : {}),
  };
}

/** The /results path (bare, no locale) a criteria opens the card at. */
export function resultsPathFor(c: ResultsCriteria | null | undefined): string {
  const qs = writeResultsParams(fromResultsCriteria(c)).toString();
  return `/results${qs ? `?${qs}` : ''}`;
}

/** What one card state adds/removes relative to another — the «Изменено: …»
 *  line under «Обновить поиск». Chip lists are named item by item (the reader
 *  typed them); the panel axes are named by axis. */
export interface ResultsChange {
  added: Array<{ kind: 'word' | 'category' | 'ikpu'; value: string }>;
  removed: Array<{ kind: 'word' | 'category' | 'ikpu'; value: string }>;
  axes: Array<'region' | 'budget' | 'exclude' | 'kind' | 'customer'>;
}

export function diffResultsState(before: ResultsCardState, after: ResultsCardState): ResultsChange {
  const out: ResultsChange = { added: [], removed: [], axes: [] };
  const lists = [
    ['word', before.words, after.words],
    ['category', before.categories, after.categories],
    ['ikpu', before.ikpu, after.ikpu],
  ] as const;
  for (const [kind, b, a] of lists) {
    for (const v of a) if (!b.includes(v)) out.added.push({ kind, value: v });
    for (const v of b) if (!a.includes(v)) out.removed.push({ kind, value: v });
  }
  const sameSet = (x: readonly string[], y: readonly string[]) =>
    x.length === y.length && x.every((v) => y.includes(v));
  if (!sameSet(before.regions, after.regions)) out.axes.push('region');
  if (before.minBudget !== after.minBudget || before.maxBudget !== after.maxBudget)
    out.axes.push('budget');
  if (!sameSet(before.exclude, after.exclude)) out.axes.push('exclude');
  if (!sameSet(before.kinds, after.kinds)) out.axes.push('kind');
  if ((before.customer ?? '') !== (after.customer ?? '')) out.axes.push('customer');
  return out;
}

export function isNoChange(c: ResultsChange): boolean {
  return c.added.length === 0 && c.removed.length === 0 && c.axes.length === 0;
}
